import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  Switch as MuiSwitch,
  TextField as MuiTextField,
  Typography,
} from '@mui/material';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { isEmpty } from 'lodash-es';
import type { FC, ReactNode } from 'react';
import { Suspense, useEffect, useMemo, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { SuspenseErrorFallback } from 'components/common';
import { usePartnerships } from 'features/cabinet/facebook';
import type { Partnership as CabinetPartnership } from 'features/cabinet/facebook/types';
import PartnershipSection, { type PartnershipAdSettingFormValue, type PartnershipOption, } from './PartnershipSection';
import PartnershipAdsToggle from './PartnershipAdsToggle';
import PartnershipSetupSettings from './PartnershipSetupSettings';
import { createDefaultPartnershipAdSetting } from '../utils/partnershipAdSettings';
import { AddOutlined } from '@mui/icons-material';

export const FormFieldSkeleton: FC = () => (
  <Skeleton height={40} sx={{ transform: 'translateY(0)', borderRadius: '8px' }} />
);

export const FormSectionDivider: FC = () => (
  <Box sx={(t) => ({ borderBottom: `1px solid ${t.palette.divider}` })} />
);

interface FormSectionSwitcherRowProps {
  title: string;
  children: ReactNode;
}

export const FormSectionSwitcherRow: FC<FormSectionSwitcherRowProps> = ({ title, children }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
    <Typography variant="subtitle1" fontWeight={500} sx={{ m: 0 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

export const HotjarSwitcher: FC = () => {
  const { control } = useFormContext();

  return (
    <Controller
      control={control}
      name="hotjar"
      render={({ field: { value, onChange } }) => (
        <MuiSwitch
          size="medium"
          checked={Boolean(value)}
          onChange={(_, nextChecked) => {
            onChange(nextChecked);
          }}
          inputProps={{
            'data-testid': 'hotjarSwitcher',
            'aria-label': 'Hotjar',
          }}
        />
      )}
    />
  );
};

export const HotjarSection: FC = () => (
  <>
    <FormSectionDivider />
    <FormSectionSwitcherRow title="Hotjar">
      <HotjarSwitcher />
    </FormSectionSwitcherRow>
  </>
);

export const PartnershipAdsSwitcherRow: FC = () => (
  <FormSectionSwitcherRow title="Partnership ads">
    <PartnershipAdsToggle showLabel={false} />
  </FormSectionSwitcherRow>
);

export const PartnershipAdsRestrictionAlert: FC = () => (
  <Alert severity="info" variant="standard" sx={{ width: 'fit-content' }}>
    Partnership ads are only available for Single Ad type
  </Alert>
);

export const AddLabelSuffixButton: FC = () => {
  const { watch, setValue } = useFormContext();
  const adLabelSuffix = (watch('adLabelSuffix') as string) ?? '';
  const [isOpen, setIsOpen] = useState(false);
  const [draftValue, setDraftValue] = useState('');

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={() => {
          setIsOpen(false);
        }}
        PaperProps={{
          sx: { maxWidth: 440, minHeight: 180, width: '90%', py: 1 },
        }}
      >
        <DialogTitle>Ad label suffix</DialogTitle>
        <DialogContent sx={{ px: 2, overflow: 'visible' }}>
          <MuiTextField
            fullWidth
            label="Ad label suffix"
            value={draftValue}
            helperText="Optional text at the end of ad label"
            onChange={(event) => {
              setDraftValue(event.target.value);
            }}
          />
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
          <Button
            variant="text"
            color="error"
            onClick={() => {
              setIsOpen(false);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setValue('adLabelSuffix', draftValue, { shouldValidate: true });
              setIsOpen(false);
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <Button
        size="small"
        variant="contained"
        color="infoFill"
        data-testid="adLabelSuffixBadge"
        sx={{
          '&': {
            display: 'flex',
            gap: 1 / 2,
            alignItems: 'center',
            whiteSpace: 'nowrap',
            textTransform: 'initial',
            pl: 1,
            minWidth: 20,
            maxWidth: 220,
          },
        }}
        onClick={() => {
          setDraftValue(adLabelSuffix);
          setIsOpen(true);
        }}
      >
        <LocalOfferIcon
          sx={(t) => ({
            fontSize: t.typography.h4.fontSize,
            color: t.palette.text.secondary,
          })}
        />
        <Typography
          variant="body2"
          component="span"
          sx={{
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            display: 'block',
            overflow: 'hidden',
          }}
        >
          {adLabelSuffix || '+ Add label suffix'}
        </Typography>
      </Button>
    </>
  );
};

export const CreateAdsHeader: FC = () => (
  <Box sx={{ display: 'flex', width: '50%', justifyContent: 'space-between', alignItems: 'center', mb: 4, gap: 2 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Typography variant="h6" gutterBottom sx={{ m: 0 }}>
        Create Ads
      </Typography>
      <AddLabelSuffixButton />
    </Box>
  </Box>
);

const AddMorePartnershipsDivider: FC<{
  disabled: boolean;
  onAdd: () => void;
}> = ({ disabled, onAdd }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
    <Box sx={(t) => ({ flex: 1, borderBottom: `1px solid ${t.palette.divider}` })} />
    <Button
      sx={{ whiteSpace: 'nowrap' }}
      variant="outlined"
      disabled={disabled}
      onClick={onAdd}
      startIcon={<AddOutlined />}
    >
      Add partner
    </Button>
    <Box sx={(t) => ({ flex: 1, borderBottom: `1px solid ${t.palette.divider}` })} />
  </Box>
);

export const PartnershipsSection: FC = () => {
  const { control, setValue, getValues } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'partnershipAdSettings',
  });
  const partnershipAdSettings = useWatch({
    name: 'partnershipAdSettings',
  }) as PartnershipAdSettingFormValue[] | undefined;
  const partnershipAdSettingsFields = useWatch({
    name: 'partnershipAdSettingsFields',
  }) as Record<string, unknown>[] | undefined;

  const { data: { data: partnershipsResponse } } = usePartnerships({
    params: { limit: 1000, offset: 1 },
  });

  const partnershipOptions = useMemo<PartnershipOption[]>(
    () =>
      partnershipsResponse
        .filter(p => p.status === 'Approved')
          .map((partnership: CabinetPartnership) => {
          const displayName =
            !isEmpty(partnership.instagram_page_name) && partnership.instagram_page_name
              ? partnership.instagram_page_name
              : partnership.facebook_page_name;

          return {
            value: String(partnership.id),
            label: displayName,
            tag: partnership.tag,
            facebookPageId: partnership.facebook_page_id,
            appPageId: partnership.app_page_id,
            appPageName: partnership.app_page_name,
            instagramPageId: partnership.instagram_page_id,
          };
        }),
    [partnershipsResponse],
  );

  const canAddPartnership = useMemo(
    () =>
      (partnershipAdSettings ?? []).every((setting) =>
        Boolean(setting.partnershipId?.trim()),
      ),
    [partnershipAdSettings],
  );

  useEffect(() => {
    const targetSettingsCount = partnershipAdSettings?.length ?? 0;
    const currentSettings = Array.isArray(partnershipAdSettingsFields) ? partnershipAdSettingsFields : [{}];

    if (currentSettings.length >= targetSettingsCount) {
      return;
    }

    const nextSettings = [...currentSettings];
    while (nextSettings.length < targetSettingsCount) {
      nextSettings.push({});
    }

    setValue('partnershipAdSettingsFields', nextSettings, { shouldValidate: true });
  }, [partnershipAdSettings?.length, partnershipAdSettingsFields, setValue]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', columnGap: 3, rowGap: 3 }}>
      {fields.map((field, index) => (
        <PartnershipSection
          key={field.id}
          index={index}
          partnershipOptions={partnershipOptions}
          canRemove={fields.length > 1}
          onRemove={() => {
            if (fields.length <= 1) {
              return;
            }
            const currentFields = Array.isArray(getValues('partnershipAdSettingsFields'))
              ? [...(getValues('partnershipAdSettingsFields') as Record<string, unknown>[])]
              : [];
            currentFields.splice(index, 1);
            setValue('partnershipAdSettingsFields', currentFields, { shouldValidate: true });
            remove(index);
          }}
        />
      ))}
      <AddMorePartnershipsDivider
        disabled={!canAddPartnership}
        onAdd={() => {
          append(createDefaultPartnershipAdSetting());
        }}
      />
      <PartnershipSetupSettings />
    </Box>
  );
};

export const PartnershipsSectionSuspense: FC = () => (
  <ErrorBoundary
    fallback={
      <SuspenseErrorFallback title="Partnerships" message="Error occur" />
    }
  >
    <Suspense fallback={<FormFieldSkeleton />}>
      <PartnershipsSection />
    </Suspense>
  </ErrorBoundary>
);
